import {dayInMS, hourInMS, minuteInMS} from "web-vcore";
import {liveSession} from "../../../Engine/LiveSessionManagement.js";
import {SessionEvent} from "../../../Engine/LiveSession/SessionEvent.js";
import type {LiveSession_Local} from "../../../Engine/LiveSession_Local.js"; // import just type, to avoid cycle
import {EngineConfig} from "../engineConfigs/@EngineConfig.js";
import {EstimateDreamEndTime, GetJournalEntries} from "../journalEntries.js";
import {JournalEntry} from "../journalEntries/@JournalEntry.js";
import {GetSessions} from "../sessions.js";
import {Session} from "../sessions/@Session.js";
import {MeID} from "../users.js";
import {EventGroup, SessionPeriod, SleepCycle} from "./SessionPeriod.js";
import {untracked} from "mobx";
import {CreateAccessor} from "mobx-graphlink";
import {AssertWarn} from "js-vextensions";
import {GetAlarmDelayCandidatesFromEngineConfig} from "../../../UI/Stats/AggregationData.js";
import {Phase} from "./Phase.js";
import {EstimateJournalEntryStartTime, EstimateSegmentStartTime} from "./JournalTimeEstimates.js";

// this is just for debugging from dev-tools
export function GetSessionPeriods_Me() {
	const journalEntries = GetJournalEntries(MeID());
	const sessions = GetSessions(MeID());
	return GetSessionPeriods(journalEntries, sessions);
}

/** On a practical level, this basically creates one session-period journal-entry (including all sessions intersecting the entry's time-range), and then one session-period for any remaining sessions (these are almost always day sessions). */
export function GetSessionPeriods(journalEntries: JournalEntry[], sessions: Session[], appendLiveSessionInfo = true, warnIfMoreThan5SessionsInOnePeriod = false): SessionPeriod[] {
	const result = [] as SessionPeriod[];

	const sessions_unclaimed = sessions.slice();
	if (appendLiveSessionInfo && liveSession && liveSession.constructor.name == "LiveSession_Local") {
		//const sessionInfoSoFar = (liveFBASession as FBASession_Local).GetSessionInfo();
		//const eventCount = untracked(()=>liveFBASession?.coreData.events.length);
		const sessionInfoSoFar = ConstructLiveSessionInfoSoFar();
		if (sessionInfoSoFar) {
			sessions_unclaimed.push(sessionInfoSoFar);
		}
	}

	// start by looping through all journal-entries, and creating a session-period for each (including intersecting sessions)
	for (const [i, entry] of journalEntries.entries()) {
		const nextEntry = journalEntries[i + 1];
		const period = ConstructSessionPeriodForJournalEntry(entry, sessions_unclaimed, nextEntry, warnIfMoreThan5SessionsInOnePeriod);
		result.push(period);

		for (const session of period.sessions) {
			sessions_unclaimed.Remove(session);
		}
	}

	// then, for each session that is not part of any journal-entry, create a session-period for it
	for (const session of sessions_unclaimed) {
		const period = ConstructSessionPeriodForUnclaimedSession(session);
		result.push(period);
	}

	// then return the session-periods, sorted by start-time
	return result.OrderBy(a=>a.startTime);
}

/** Use this as a cache-point for constructing the live-session info-so-far.
 * It only is re-calced when the live-session event-count changes, OR when one of the mobx fields accessed in GetSessionInfo() is modified. */
export const ConstructLiveSessionInfoSoFar = CreateAccessor(()=>{
	const tracker1_eventCount = liveSession?.coreData.events.length;
	return liveSession?.AsLocal?.GetSessionInfo();
});

export const ConstructSessionPeriodForJournalEntry = CreateAccessor((entry: JournalEntry, sessionsUnclaimedSoFar: Session[], nextEntry: JournalEntry|n, warnIfMoreThan5SessionsInOnePeriod = false)=>{
	/*if (liveFBASession && sessionsInRange.Any(a=>a.startTime == liveFBASession!.coreData.startTime)) {
		console.log("Reconstructing session-period for live-session. EventGroups:", eventGroups);
	}*/

	const startTime_fromEntryAlone = EstimateJournalEntryStartTime(entry);
	const endTime_fromEntryAlone = EstimateDreamEndTime(entry);
	let sleepStartTime = entry.segments.filter(a=>a.wakeTime == null).map(a=>EstimateSegmentStartTime(entry, a)).OrderBy(a=>a)[0] as number|n;

	const sessionsToAssociate =
		// if we know the journal-entry end-time, find all sessions that start within its time-range
		endTime_fromEntryAlone != null ? sessionsUnclaimedSoFar.filter(session=>{
			const sessionStartsAtOrAfterThisEntryStart = session.startTime >= startTime_fromEntryAlone;
			//const sessionIsBeforeNextEntry = nextEntry == null || (nextEntry.sleepTime && session.startTime < nextEntry.sleepTime);
			//return sessionStartsAtOrAfterThisEntry && sessionIsBeforeNextEntry;
			const sessionStartsAtOrBeforeThisEntryEnd = session.startTime <= endTime_fromEntryAlone!;
			return sessionStartsAtOrAfterThisEntryStart && sessionStartsAtOrBeforeThisEntryEnd;
		}) :
		// else, if this journal-entry (with no end-time) started in the last 18hr, then associate all *night* sessions started at or after the journal-entry's start-time (needed for the session-period of live-session)
		(startTime_fromEntryAlone >= Date.now() - (18 * hourInMS)) ? sessionsUnclaimedSoFar.filter(session=>{
			return session.startTime >= startTime_fromEntryAlone && session.launchType == "night";
		}) :
		// else, this journal-entry is just "flawed", by never having had an end-time set; don't associate any sessions with it (the journal-entry has too little data for clean association)
		[];
	if (sessionsToAssociate.length > 5 && warnIfMoreThan5SessionsInOnePeriod) {
		console.warn("More than 5 sessions were found in range for a single journal-entry. This *might* indicate a bug, or bad data.");
		console.log("Journal-entry start-time:", entry.sleepTime ? new Date(entry.sleepTime).toLocaleString("sv") : "n/a");
		console.log("Next journal-entry start-time:", nextEntry?.sleepTime ? new Date(nextEntry.sleepTime).toLocaleString("sv") : "n/a");
		for (const [i, session] of sessionsToAssociate.entries()) {
			console.log(`#${i + 1} session start-time:`, new Date(session.startTime).toLocaleString("sv"));
		}
		debugger;
	}

	const eventGroups = GetEventGroupsInSessions(sessionsToAssociate);
	const sleepCycles = DiscernSleepCyclesFromJournalEntry(entry, sessionsToAssociate, eventGroups);

	// expand start/end times to include all sessions that intersect the source journal-entry (also push-back the sleep-start-time, if there's a night-session that started earlier)
	let startTime_final = startTime_fromEntryAlone;
	let endTime_final = endTime_fromEntryAlone;
	for (const session of sessionsToAssociate) {
		if (session.startTime < startTime_final) startTime_final = session.startTime;
		if (session.endTime && session.endTime > (endTime_final ?? 0)) endTime_final = session.endTime;
		if (session.launchType == "night" && session.startTime < (sleepStartTime ?? Infinity)) sleepStartTime = session.startTime;
	}

	return new SessionPeriod({
		startTime: startTime_final,
		endTime: endTime_final,
		sleepStartTime,
		sessions: sessionsToAssociate,
		eventGroups,
		journalEntries: [entry],
		journalSegments: entry.segments,
		sleepCycles,
	});
});

export const ConstructSessionPeriodForUnclaimedSession = CreateAccessor((session: Session)=>{
	const sleepCycles = [ConstructFakeSleepCycleForStandaloneSession(session)];
	return new SessionPeriod({
		startTime: session.startTime,
		endTime: session.endTime,
		sessions: [session],
		eventGroups: GetEventGroupsInSessions([session]),
		journalEntries: [],
		journalSegments: [],
		sleepCycles,
	});
});

export function GetEventGroupsInSessions(sessions: Session[]): EventGroup[] {
	const sessionEventsInRange = sessions.SelectMany(a=>{
		return [
			// add synthetic event, for start of session
			new SessionEvent({date: a.startTime, type: "General.SessionStart", _isSynthetic: true}),
			...a.events,
			// add synthetic event, for end of session
			new SessionEvent({date: a.endTime ?? Date.now(), type: "General.SessionEnd", _isSynthetic: true}),
		];
	}).OrderBy(a=>a.date);

	const eventGroups = [] as EventGroup[];

	type EventGroup_BeingConstructed = PartialBy<EventGroup, "sessionInfo" | "type" | "duration">;
	let currentGroup = {events: [] as SessionEvent[]} as EventGroup_BeingConstructed;
	const endCurrentGroup = ()=>{
		currentGroup.duration = currentGroup.events.Last().date - currentGroup.events.First().date;
		const groupFirstNonSyntheticEvent = currentGroup.events.find(a=>!a._isSynthetic);
		currentGroup.sessionInfo = sessions.find(session=>{
			//return session.events.ContainsAny(...currentGroup.events);
			return session.events.includes(groupFirstNonSyntheticEvent as any);
		});
		// only push event-group if it has an actual session associated (else breaks typescript non-null assertion) [can happen if group has only synthetic events]
		if (currentGroup.sessionInfo != null) {
			eventGroups.push(currentGroup as EventGroup);
		}
		currentGroup = {events: []} as EventGroup_BeingConstructed;
	};
	for (const [i, event] of sessionEventsInRange.entries()) {
		//const prevEvent: SessionEvent|n = sessionEventsInRange[i - 1];
		let currentPhase = Phase.notStarted as Phase;

		let isGroupEnder = false;
		// events for group-type: special
		if (event.type == "General.SessionRecovered") {
			// end previous group (session-recovery should be its own group)
			if (currentGroup.events.length) endCurrentGroup();
			currentGroup.type = "special";
			// end new group (session-recovery should be its own group)
			isGroupEnder = true;
		}
		// events for group-type: wait-period
		if (event.type == "General.SessionStart") {
			// if a group is already in-progress, end it now (can't have groups span multiple sessions)
			if (currentGroup.events.length) endCurrentGroup();
		} else if (event.type == "General.SessionEnd") {
			isGroupEnder = true;
		} else if (event.type == "General.InitialDelayEnd") {
			currentGroup.type = "wait-period";
			isGroupEnder = true;
		} else if (event.type == "Alarms.PhaseSet" && event.phase != null) {
			// if moving into drifting-phase, then consider a drifting-start, and set the current group type
			if (event.phase == Phase.drifting) {
				currentGroup.type = "wait-period";
			} else {
				// if moving out from drifting-phase (and into a group other than not-started/session-ending), then consider a drifting-end, and end the current group
				if (currentPhase == Phase.drifting && event.phase != Phase.notStarted) {
					isGroupEnder = true;
				}
			}
			currentPhase = event.phase;
		} else if (event.type == "Alarms.PhaseSkip") {
			isGroupEnder = true;
		}
		// events for group-type: cycle
		else if (event.type == "Alarms.SolveStart") {
			currentGroup.type = "cycle";
		} else if (event.type == "Alarms.SolveFail") {
			isGroupEnder = true;
		} else if (event.type == "Alarms.SolveSuccess") {
			isGroupEnder = true;
		}

		currentGroup.events.push(event);
		if (isGroupEnder) endCurrentGroup();
	}
	if (currentGroup.events.length) endCurrentGroup();
	return eventGroups;
}

export function ConstructFakeSleepCycleForStandaloneSession(session: Session) {
	return new SleepCycle({
		cycleNumber: 0, // this marks the sleep-cycle as "fake"
		startTime: session.startTime,
		endTime: session.endTime,
		associatedDreamSegments: [],
		associatedSessions: [session],
		associatedEventGroups: GetEventGroupsInSessions([session]),
		alarmDelay: 0,
		alarmWaitEndTime: 0,
		responseTime: 0,
	});
}

export function DiscernSleepCyclesFromJournalEntry(
	dream: JournalEntry,
	sessionsInRange: Session[], eventGroups: EventGroup[],
) {
	const result = [] as SleepCycle[];

	const newCycle = ()=>new SleepCycle({
		cycleNumber: result.length + 1,
		startTime: 0,
		endTime: 0,
		// these arrays all start out empty, but can get populated by the two loops below (through dream-segments, and through sessions)
		associatedDreamSegments: [],
		associatedSessions: [],
		associatedEventGroups: [],
		alarmDelay: 0,
		alarmWaitEndTime: 0,
		responseTime: 0,
	});

	if (dream.segments.length) {
		let lastCycle: SleepCycle|n;
		let currentCycle = newCycle();
		for (const [i, segment] of dream.segments.entries()) {
			if (currentCycle.startTime == 0) {
				currentCycle.startTime = lastCycle?.endTime || EstimateSegmentStartTime(dream, segment);
			}

			currentCycle.associatedDreamSegments.push(segment);
			if (segment.wakeTime != null && currentCycle.associatedDreamSegments.filter(a=>a.wakeTime == null).length) {
				currentCycle.endTime = segment.wakeTime;
				result.push(currentCycle);
				lastCycle = currentCycle;
				currentCycle = newCycle();
			}
		}
		if (currentCycle.associatedDreamSegments.length) {
			//currentCycle.endTime = EstimateSegmentEndTime(dream, currentCycle.associatedDreamSegments.Last());
			result.push(currentCycle);
		}
	}

	//Assert(!infoForAssociation?.segments.Any(a=>a.wakeTime != null), "FindCycleNumberForDreamSegment should only be called for non-wake segments.");
	const groupIsAlarmWait = (group: EventGroup)=>{
		// for drifting-group: WORKS (ends with PhaseSet=>dreaming)
		// for dreaming-group, ended by micro-snooze: WORKS (ends with SolveSuccess/Fail event)
		// for dreaming-group, ended by breath-comp: FAILS
		// ...(no code set up to detect breath-comp dreaming-end as group end, so dreaming-group gets "merged" with next drifting-group)
		// SO, TODO: solve the fail case (probably while simplifying the logic in general)
		const driftingEndIndexes = GetIndexesOfDriftingEndEvents(group.events);
		return group.type == "wait-period" && driftingEndIndexes.length > 0;
	};

	const augmentCycleSessionsFromEventGroups = (cycle: SleepCycle, eventGroups: EventGroup[])=>{
		for (const eventGroup of eventGroups) {
			if (!cycle.associatedSessions.includes(eventGroup.sessionInfo)) {
				cycle.associatedSessions.push(eventGroup.sessionInfo);
			}
		}
	}

	let alarmWaitsReached = 0;
	let lastAlarmWait_delay = 0;
	let lastAlarmWait_endTime: number|n;
	const eventGroupsForNextSleepCycleAugment = [] as EventGroup[];
	for (const [i, group] of eventGroups.entries()) {
		//const priorGroups = eventGroups.slice(0, i);
		const lastEvent = group.events.Last();
		eventGroupsForNextSleepCycleAugment.push(group);

		if (groupIsAlarmWait(group)) {
			alarmWaitsReached++;
			// if the session had the alarm-delay locked to a single value, prefer that over duration inferred from the session-events
			// (the session-events can be complicated by eg. manual resets of sleep-timer, so a known locked alarm-delay is more reliable)
			lastAlarmWait_delay = group.sessionInfo?.alarmDelay ?? group.duration;
			lastAlarmWait_endTime = lastEvent.date;
		} else if (group.type == "cycle" && group.events.Any(a=>a.type == "Alarms.SolveSuccess") && lastAlarmWait_endTime != null) {
			const cycleNumber = alarmWaitsReached; // the "first real cycle" comes after the first alarm-wait
			const graphGroup_alarmDelay = (lastAlarmWait_delay / minuteInMS).RoundTo(1) ?? 0;

			const userResponseEvent = /*group.events.find(a=>a.type == "Journey.ListenStart") ??*/ lastEvent;
			const responseTime = userResponseEvent.date - lastAlarmWait_endTime;
			if (responseTime > dayInMS && !g["warningDone_responseTimeForCycleTooLong"]) {
				g["warningDone_responseTimeForCycleTooLong"] = true; // only show this warning once
				AssertWarn(false, `Response-time for cycle ${cycleNumber} is too long: ${responseTime / hourInMS}h (thus ignoring cycle)`);
				continue;
			}

			// journal-segment gets added by engine first; so find the newest segment that ended before this "Journey.CycleSuccess" event
			const bestSleepCycleMatch = result.findLast(a=>a.endTime && a.endTime < lastEvent.date);
			if (bestSleepCycleMatch) {
				bestSleepCycleMatch.alarmDelay ||= graphGroup_alarmDelay;
				bestSleepCycleMatch.alarmWaitEndTime ||= lastAlarmWait_endTime;
				bestSleepCycleMatch.responseTime ||= responseTime;
				bestSleepCycleMatch.associatedEventGroups.push(...eventGroupsForNextSleepCycleAugment);
				augmentCycleSessionsFromEventGroups(result.Last(), eventGroupsForNextSleepCycleAugment);
				eventGroupsForNextSleepCycleAugment.Clear();
			}
		}
	}
	// any event-groups at end, apply to last sleep-cycle (add their sessions as well)
	if (eventGroupsForNextSleepCycleAugment.length && result.length) {
		result.Last().associatedEventGroups.push(...eventGroupsForNextSleepCycleAugment);
		augmentCycleSessionsFromEventGroups(result.Last(), eventGroupsForNextSleepCycleAugment);
	}

	// todo: maybe make this temporary/selectively-applied: for any "cycle" that is 160+ minutes long, split it into two cycles (at the 80-minute mark), then repeat till all cycles are <160 minutes
	let fakeCyclesAdded = 0;
	const cycleOrigCycleNumbers = new Map<SleepCycle, number>();
	for (let i = 0; i < result.length; i++) {
		const cycle = result[i];
		const closestSession = sessionsInRange.map(session=>{
			const sessionStart = session.startTime;
			const sessionEnd = session.endTime ?? Date.now();
			const cycleStart = cycle.startTime;
			const cycleEnd = cycle.endTime ?? Date.now();

			// find the time gap between session's time-range and cycle's time-range (if intersecting, gap is 0)
			if (sessionStart <= cycleEnd && sessionEnd >= cycleStart) { // check if the ranges overlap
				return {session, gap: 0}; // ranges overlap, gap is 0
			} else if (sessionEnd < cycleStart) { // session ends before cycle starts
				return {session, gap: cycleStart - sessionEnd};
			} else { // session starts after cycle ends
				return {session, gap: sessionStart - cycleEnd};
			}
		}).sort((a, b)=>a.gap - b.gap)[0]; // sort by gap and take the smallest gap
		//const closestSession_alarmDelay = GetAlarmDelayCandidatesFromEngineConfig(closestSession.session.config).Min();
		const closestSession_alarmDelay = closestSession?.session.alarmDelay; // in ms

		// todo: have this hack only used when a special param is sent (for stats calculation, if option is enabled for that view)
		if (closestSession && closestSession_alarmDelay) {
			if (cycle.endTime && cycle.endTime - cycle.startTime >= 2 * closestSession_alarmDelay) {
				const splitTime = cycle.startTime + closestSession_alarmDelay;
				const newCycle = new SleepCycle({
					cycleNumber: cycle.cycleNumber,
					startTime: cycle.startTime,
					endTime: splitTime,
					alarmDelay: cycle.alarmDelay,
					alarmWaitEndTime: cycle.alarmWaitEndTime,
					responseTime: cycle.responseTime,
					associatedSessions: cycle.associatedSessions,
					// todo: confirm this handling is the best way
					associatedDreamSegments: [], //cycle.associatedDreamSegments,
					associatedEventGroups: [], //cycle.associatedEventGroups,
					syntheticallyAddedSleepCycle: true, // marks this entry as synthetic, so it doesn't also count as a lucid (if source cycle was lucid)
				});
				result.Insert(i, newCycle);
				fakeCyclesAdded++;

				//cycle.startTime += closestSession_alarmDelay;
				cycle.startTime = splitTime;
				if (!cycleOrigCycleNumbers.has(cycle)) cycleOrigCycleNumbers.set(cycle, cycle.cycleNumber);
				cycle.cycleNumber = cycleOrigCycleNumbers.get(cycle)! + fakeCyclesAdded;
			}
		}
	}

	// temp; associating [event-groups + sessions] with cycles is apparently unreliably atm, so just add all sessions to all cycles (needed for isDreamCycle check in AggegationData.ts)
	// (not sure why it's unreliably; maybe from a cycle's single event coming just a moment before/after the split point in segments)
	for (const cycle of result) {
		cycle.associatedSessions.push(...sessionsInRange.Exclude(...cycle.associatedSessions));
	}

	return result;
}

export function Legacy_GetInitialDelay(config: EngineConfig, fallbackValue = 0) {
	return config.general?.initialDelay ?? config["initialDelay"] ?? fallbackValue;
}
export function Legacy_FirstSolvingPhaseCountsAsRehearsalSolveForConfig(config: EngineConfig) {
	return Legacy_GetInitialDelay(config) <= 60000;
};

/*export function GetMaxPossibleSolveSuccesses(sessionInfosInRange: Session[], eventGroups: EventGroup[]) {
	if (sessionInfosInRange.length == 0) return 0;
	// fallback for ancient sessions with configs that miss needed data
	const session1Conf = sessionInfosInRange[0].config;
	if (session1Conf.journeyVisualization?.cycleReverse_minTime == null) return 0;
	if (session1Conf.alarms?.alarmDelay_pool == null) return 0;
	if (session1Conf.journeyGrid?.targetDelay_minTime == null) return 0;

	const totalTime = sessionInfosInRange.map(a=>a.endTime ?? Date.now()).Max() - sessionInfosInRange.map(a=>a.startTime).Min();
	const timeForInitDelays = sessionInfosInRange.map(a=>Legacy_GetInitialDelay(a.config)).Sum();
	// number of sessions that were "possible to have had a successful initial-cycle in", ie. those which completed their initial-delay
	const nonRehearsalInitCycles = sessionInfosInRange.filter(a=>!Legacy_FirstSolvingPhaseCountsAsRehearsalSolveForConfig(a.config) && a.events.Any(a=>a.type == "Alarms.SolveStart")).length;

	const timeForAlarmWaitPlusCyclePairs = totalTime - timeForInitDelays;

	const cycleType = sessionInfosInRange.Any(a=>a.events.Any(b=>b.type == "Journey.CycleReverse")) ? "visualization" : "grid";
	// Should the alarm-delay "min" be used here, or the "average"? Not sure...
	const alarmWaitPlusCyclePair_minLength = cycleType == "visualization"
		? sessionInfosInRange[0].config.journeyVisualization.cycleReverse_minTime + sessionInfosInRange[0].config.alarms.alarmDelay_pool.Min()
		: sessionInfosInRange[0].config.journeyGrid.targetDelay_minTime + sessionInfosInRange[0].config.alarms.alarmDelay_pool.Min();

	return nonRehearsalInitCycles + (timeForAlarmWaitPlusCyclePairs / alarmWaitPlusCyclePair_minLength).FloorTo(1);
}*/

export function EventGroupWasRehearsalSolve(group: EventGroup, allGroups: EventGroup[]) {
	// if this event-group is not even a cycle group (eg. instead an alarm-wait period), then it can't be a rehearsal-cycle
	//if (!group.events.Any(a=>a.type == "Journey.CycleStart")) return false;
	if (group.type != "cycle") return false;

	const session = group.sessionInfo;
	const groupsInSession = allGroups.filter(a=>a.sessionInfo == session);
	const priorGroupsInSession = groupsInSession.slice(0, groupsInSession.indexOf(group));
	const priorGroupsInSession_lastIndexOfRehearsallyInitDelayOrDriftingSkip = priorGroupsInSession.SelectMany(a=>a.events).findLastIndex(a=>{
		return (session && Legacy_FirstSolvingPhaseCountsAsRehearsalSolveForConfig(session.config) && a.type == "General.InitialDelayEnd") || a.type == "Alarms.PhaseSkip";
	});
	const priorGroupsInSession_events = priorGroupsInSession.SelectMany(a=>a.events);
	//const priorGroupsInSession_lastIndexOfAlarmWaitEnd = priorGroupsInSession_events.findLastIndex(a=>a.type == "Journey.ResleepEnd");
	const priorGroupsInSession_lastIndexOfDriftingEnd = GetIndexesOfDriftingEndEvents(priorGroupsInSession_events).LastOrX() ?? -1;
	// if initial-delay or last-alarm-wait-skip (before target cycle-group) was closer to target cycle-group than the last alarm-wait-end, then the cycle was a "rehearsal"
	// (the idea of a "rehearsal cycle" is one that was the first of the night [if initial delay was <1m], or it was skipped to -- ie. not organic / interacted with after actual awakening)
	return priorGroupsInSession_lastIndexOfRehearsallyInitDelayOrDriftingSkip > priorGroupsInSession_lastIndexOfDriftingEnd;
}

export function GetIndexesOfDriftingEndEvents(events: SessionEvent[]) {
	const result = [] as number[];
	let currentPhase = Phase.notStarted as Phase;
	for (let i = 0; i < events.length; i++) {
		const event = events[i];
		if (event.type == "Alarms.PhaseSet" && event.phase != null) {
			if (event.phase != Phase.drifting && currentPhase == Phase.drifting) {
				result.push(i);
			}
			currentPhase = event.phase;
		}
	}
	return result;
}